import { useSnackbar } from "notistack";
import { useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  createChildCompanyAdmin,
  createCompanyAdmin,
  createCompanyAdminByAdmin,
  createStaff,
  deleteStaff,
  getStaffInfo,
  getStaffList,
  updateStaffDetailsById,
  uploadStaffImage,
} from "../api-services/brokers";
import { QUERY_CACHE_TIME, STAFF_ROLE, USER_ROLE } from "../commons/enum";
import { useIsLoggedIn, useMyInfo } from "./user.hooks";

export const useMyStaffInfo = () => {
  const isLoggedIn = useIsLoggedIn();
  return useQuery("myStaffInfo", () => getStaffInfo(), {
    enabled: isLoggedIn,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useIsSupportRole = () => {
  const { data: staffInfo } = useMyStaffInfo();
  const { data: userInfo } = useMyInfo();
  return useMemo(() => {
    if (!staffInfo?.staffRoles || !userInfo) return false;
    if (userInfo.userRole !== USER_ROLE.STAFF) return false;
    return staffInfo.staffRoles.some(
      (role) => role.role === STAFF_ROLE.SUPPORT
    );
  }, [staffInfo, userInfo]);
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteStaff, {
    onSuccess: () => {
      return queryClient.invalidateQueries("staffList");
    },
  });
};

export const useStaffList = (
  { search, companyIds, sortBy, sortDirection } = {},
  limit = 10,
  page = 1
) => {
  const isLoggedIn = useIsLoggedIn();
  const { data: userInfo } = useMyInfo();
  const { data: staffInfo } = useMyStaffInfo();
  const isCompanyAdminOrSupport = staffInfo?.staffRoles.some(
    (role) =>
      role.role === STAFF_ROLE.COMPANY_ADMIN || role.role === STAFF_ROLE.SUPPORT
  );
  const isEnabled = useMemo(() => {
    return (
      isLoggedIn &&
      (userInfo?.userRole === USER_ROLE.ADMIN || isCompanyAdminOrSupport)
    );
  }, [isLoggedIn, userInfo, isCompanyAdminOrSupport]);
  return useQuery(
    ["staffList", search, companyIds, sortBy, sortDirection, limit, page],
    () =>
      getStaffList({ search, companyIds, sortBy, sortDirection }, limit, page),
    {
      enabled: isEnabled,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
    }
  );
};

export const useUpdateStaffImage = () => {
  return useMutation(uploadStaffImage);
};

export const useUpdateStaffById = ({ selfUpdate = false }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation(
    (data) => {
      return updateStaffDetailsById(data.id, data.params);
    },
    {
      onSuccess: () => {
        if (selfUpdate) {
          queryClient.invalidateQueries("myStaffInfo");
          enqueueSnackbar("Your information has been updated", {
            variant: "success",
          });
        }
      },
    }
  );
};

export const useCreateCompanyAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(createCompanyAdmin, {
    onSuccess: () => {
      return queryClient.invalidateQueries("companyList");
    },
  });
};

export const useCreateChildCompanyAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(createChildCompanyAdmin, {
    onSuccess: () => {
      return queryClient.invalidateQueries("companyList");
    },
  });
};

export const useCreateCompanyAdminByAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(createCompanyAdminByAdmin, {
    onSuccess: () => {
      return queryClient.invalidateQueries("companyList");
    },
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  return useMutation(createStaff, {
    onSuccess: () => {
      return queryClient.invalidateQueries("staffList");
    },
  });
};

export const useInfiniteStaffList = (
  { search, companyIds, includeStatistics, sortBy, sortDirection } = {},
  limit = 10
) => {
  const isLoggedIn = useIsLoggedIn();

  return useInfiniteQuery(
    [
      "infiniteStaffList",
      search,
      companyIds,
      includeStatistics,
      sortBy,
      sortDirection,
      limit,
    ],
    ({ pageParam = 1 }) =>
      getStaffList(
        { search, companyIds, includeStatistics, sortBy, sortDirection },
        limit,
        pageParam
      ),
    {
      enabled: isLoggedIn,
      getNextPageParam: (lastPage, allPages) => {
        /**
         * * Check if there are more pages available
         */
        const currentPageNumber = allPages.length;
        const totalPages = Math.ceil(lastPage.total / limit);
        return currentPageNumber < totalPages
          ? currentPageNumber + 1
          : undefined;
      },
    }
  );
};
